import React, { useState } from 'react';
import { ExternalLink, Rocket, Calendar, ChevronDown, ChevronUp, FileText, Download } from 'lucide-react';
import { WhitepaperCard } from '../components/WhitepaperCard';

const whitepapers = [
  {
    slug: 'operational-understanding',
    title: 'From Robot Data to Operational Understanding',
    date: 'September 2026',
    summary: 'How deployed robot fleets turn telemetry, logs, video, and sensor data into a continuous loop of collection, investigation, and improvement.',
    points: [
      'Why robot data becomes a fleet-scale problem as deployments grow',
      'The missing data loop: Collect → Surface → Investigate → Improve',
      'A practical architecture for deployed robot data and the five capabilities it needs',
      'How the same workflow supports debugging, evaluation, simulation, and training',
    ],
    pdfUrl: '/whitepapers/Momentedge_Whitepaper_Sep2026.pdf',
  },
  {
    slug: 'data-problem',
    title: 'The Data Problem in Autonomous Systems',
    summary: "What are the data collection pain points of today's robotics world and the complexities of implementing the right solution.",
    points: [
      'Understand the 99% noise problem in autonomous data',
      'Learn about edge-to-cloud data pipeline challenges',
      'Discover solutions for efficient data collection',
    ],
    pdfUrl: 'https://drive.google.com/file/d/1aH9h-ObX4q157uCnQBeRrdHkuJOqZCti/view',
  },
];

const Resources = () => {
  const [expandedBlogPost, setExpandedBlogPost] = useState(null);

  // SEO: Set page title and meta
  React.useEffect(() => {
    document.title = 'Resources: Whitepapers & Updates | Momentedge';
    
    // Update meta description for this page
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Download Momentedge whitepapers on turning deployed robot data into operational understanding, and read the latest Momentedge Clipper release reports.');
    }
  }, []);

  React.useEffect(() => {
    if (window.location.hash === '#whitepapers') {
      document.getElementById('whitepapers')?.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  return (
    <div className="min-h-screen pt-20">
      {/* Continuous dark background */}
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950"></div>
        <div className="absolute inset-0 opacity-[0.06]" style={{
          backgroundImage: `
            linear-gradient(45deg, rgb(100, 116, 139) 1px, transparent 1px),
            linear-gradient(-45deg, rgb(100, 116, 139) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px'
        }}></div>
      </div>

      {/* Blog & Updates Section */}
      <section className="relative pt-10 pb-6 md:pt-12 md:pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">
              Blog & Updates
            </h2>

            {/* Blog Post 0 - Whitepaper Launch (Latest) */}
            <div className="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 shadow-xl overflow-hidden mb-6" data-testid="blog-post-whitepaper-launch">
              <div className="p-8 md:p-10">
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <div className="inline-flex items-center space-x-2 px-3 py-1 bg-blue-500/20 border border-blue-500/30 rounded-full text-blue-400 text-sm font-medium">
                    <Calendar size={14} />
                    <span>September 2026</span>
                  </div>
                  <div className="inline-flex items-center space-x-2 px-3 py-1 bg-emerald-500/20 border border-emerald-500/30 rounded-full text-emerald-400 text-sm font-medium">
                    <FileText size={16} />
                    <span>Whitepaper</span>
                  </div>
                </div>

                <h3 className="text-2xl md:text-3xl font-bold text-white mb-6">
                  New Whitepaper: From Robot Data to Operational Understanding
                </h3>

                <div className="prose prose-invert max-w-none">
                  <p className="text-lg text-slate-300 leading-relaxed mb-4">
                    As more robots are deployed, fleets generate more operational evidence than teams can manually keep up with. Known events can be instrumented, but real-world deployments keep producing unknown ones — and investigating them means searching across telemetry, logs, video, and sensor data.
                  </p>
                  <p className="text-slate-300 leading-relaxed mb-6">
                    Our new whitepaper lays out why this becomes a fleet-scale problem, the missing data loop between collection and analysis, and a practical Collect → Surface → Investigate → Improve architecture for deployed robot data — including where Momentedge Clipper fits on the known-event side.
                  </p>
                </div>

                <a
                  href="#whitepapers"
                  data-testid="blog-whitepaper-download-link"
                  className="inline-flex items-center space-x-2 px-6 py-3 bg-white hover:bg-slate-100 text-slate-900 rounded-lg font-semibold transition-colors"
                >
                  <Download size={18} />
                  <span>Download the Whitepaper</span>
                </a>
              </div>
            </div>

            {/* Blog Post 1 - Rust Support */}
            <div className="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 shadow-xl overflow-hidden mb-6">
              <div className="p-8 md:p-10">
                {/* Header with Date and Category */}
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <div className="inline-flex items-center space-x-2 px-3 py-1 bg-blue-500/20 border border-blue-500/30 rounded-full text-blue-400 text-sm font-medium">
                    <Calendar size={14} />
                    <span>July 28, 2026</span>
                  </div>
                  <div className="inline-flex items-center space-x-2 px-3 py-1 bg-purple-500/20 border border-purple-500/30 rounded-full text-purple-400 text-sm font-medium">
                    <Rocket size={16} />
                    <span>Launch Report</span>
                  </div>
                </div>

                {/* Title */}
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-6">
                  Introducing Rust Support for Momentedge Clipper
                </h3>

                {/* Excerpt */}
                <div className="prose prose-invert max-w-none">
                  <p className="text-lg text-slate-300 leading-relaxed mb-4">
                    Autonomous systems generate enormous volumes of operational data, but the event that explains a failure may last only seconds. Finding those moments—and preserving enough context to understand them—remains a time-consuming problem for robotics teams.
                  </p>
                  <p className="text-slate-300 leading-relaxed mb-6">
                    Today, we are releasing Rust support for Momentedge Clipper, our open-source event recorder for ROS 2 systems.
                  </p>
                </div>

                {/* Expand/Collapse Button */}
                <button
                  onClick={() => setExpandedBlogPost(expandedBlogPost === 'rust-support' ? null : 'rust-support')}
                  className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 font-medium transition-colors mb-6"
                >
                  {expandedBlogPost === 'rust-support' ? (
                    <>
                      <span>Show Less</span>
                      <ChevronUp size={18} />
                    </>
                  ) : (
                    <>
                      <span>Read Full Post</span>
                      <ChevronDown size={18} />
                    </>
                  )}
                </button>

                {/* Full Content (Expandable) */}
                {expandedBlogPost === 'rust-support' && (
                  <div className="prose prose-invert max-w-none border-t border-slate-700 pt-6 mt-6">
                    <h4 className="text-xl font-bold text-white mb-3">What Clipper Does</h4>
                    <p className="text-slate-300 leading-relaxed mb-4">
                      Clipper runs at the edge and maintains a rolling buffer of ROS 2 data. When an event is triggered, it preserves a configurable window before and after that moment across the relevant topics. The result is a focused MCAP recording containing the evidence engineers need, without requiring them to retain and search every second of operation.
                    </p>
                    <p className="text-slate-300 leading-relaxed mb-6">
                      Clipper uses standard MCAP files, is available under the Apache 2.0 license and does not introduce a proprietary recording format.
                    </p>
                    <p className="text-slate-300 leading-relaxed mb-6">
                      Explore the project on GitHub:{' '}
                      <a 
                        href="https://github.com/Momentedge/momentedge-clipper"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-400 hover:text-blue-300 underline font-medium"
                      >
                        https://github.com/Momentedge/momentedge-clipper
                      </a>
                    </p>

                    <h4 className="text-xl font-bold text-white mb-3 mt-8">Why Rust</h4>
                    <p className="text-slate-300 leading-relaxed mb-4">
                      An edge recorder shares constrained computing resources with the autonomy stack. It therefore needs consistent performance, predictable resource usage and strong protection against memory-related failures.
                    </p>
                    <p className="text-slate-300 leading-relaxed mb-4">
                      Rust is well suited to that environment. It provides memory safety without garbage collection and gives developers fine-grained control over performance and resource consumption. The broader robotics ecosystem is also adopting Rust through projects such as Zenoh and iceoryx2.
                    </p>
                    <p className="text-slate-300 leading-relaxed mb-6">
                      Our earliest Clipper prototype was built in Rust on an NVIDIA Jetson Orin NX. Bringing Rust support into the open-source project returns to those origins and gives teams another option for deploying Clipper on resource-constrained robotic systems.
                    </p>
                    <p className="text-slate-300 leading-relaxed mb-6">
                      Existing Python and ROS 2 workflows remain supported; Rust is an additional implementation path.
                    </p>

                    <h4 className="text-xl font-bold text-white mb-3 mt-8">Capturing an Event Is Only the Beginning</h4>
                    <p className="text-slate-300 leading-relaxed mb-4">
                      Our conversations with robotics teams have reinforced an important lesson: teams do not always know in advance which events matter, and they rarely have time to inspect hours of video, telemetry and sensor logs manually.
                    </p>
                    <p className="text-slate-300 leading-relaxed mb-6">
                      That is why Momentedge's longer-term work extends beyond recording. We are developing a cloud intelligence layer that helps teams identify important incidents, connect evidence across multiple data sources, recognize patterns across deployments and determine what to investigate next.
                    </p>
                    <p className="text-slate-300 leading-relaxed mb-6">
                      The objective is not to replace engineering judgment. It is to give engineers a shorter, evidence-backed list of events requiring their attention—and the surrounding context needed to understand them.
                    </p>

                    <h4 className="text-xl font-bold text-white mb-3 mt-8">Helping Fleets Learn from Experience</h4>
                    <p className="text-slate-300 leading-relaxed mb-4">
                      Edge capture and cloud intelligence become more valuable when they inform a shared understanding of fleet operations. Clipper creates structured evidence around known events, while broader analysis can help teams recognize patterns that would otherwise remain fragmented across logs and deployments.
                    </p>
                    <p className="text-slate-300 leading-relaxed mb-6">
                      Over time, insights from fleet operations can help teams make better decisions about what to monitor, investigate and improve. Our vision is a future in which every significant incident contributes to safer, more reliable autonomous fleets.
                    </p>
                    <p className="text-slate-300 leading-relaxed mb-6">
                      We are developing this next layer through customer discovery, product experimentation and conversations with fleet operators. If your team operates robots or autonomous vehicles and spends too much time searching operational logs, we would like to learn from your experience.
                    </p>
                    <p className="text-slate-300 leading-relaxed">
                      Try Clipper, open an issue on GitHub or contact us to compare notes about the moments your fleet needs to capture.
                    </p>
                  </div>
                )}

                {/* Footer with Link */}
                <div className="mt-6 pt-6 border-t border-slate-700">
                  <a
                    href="https://github.com/Momentedge/momentedge-clipper"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-2 text-blue-400 hover:text-blue-300 font-medium transition-colors"
                  >
                    <span>View on GitHub</span>
                    <ExternalLink size={16} />
                  </a>
                </div>
              </div>
            </div>

            {/* Blog Post 2 - Original Launch Report (v0.1.0) */}
            <div className="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 shadow-xl overflow-hidden mb-6">
              <div className="p-8 md:p-10">
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <div className="inline-flex items-center space-x-2 px-3 py-1 bg-blue-500/20 border border-blue-500/30 rounded-full text-blue-400 text-sm font-medium">
                    <Calendar size={14} />
                    <span>June 15, 2026</span>
                  </div>
                  <div className="inline-flex items-center space-x-2 px-3 py-1 bg-purple-500/20 border border-purple-500/30 rounded-full text-purple-400 text-sm font-medium">
                    <Rocket size={16} />
                    <span>Launch Report</span>
                  </div>
                  <a
                    href="https://github.com/Momentedge/momentedge-clipper/releases/tag/v0.1.0"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-1 text-blue-400 hover:text-blue-300 text-sm font-medium transition-colors ml-auto"
                  >
                    <span>View Release</span>
                    <ExternalLink size={16} />
                  </a>
                </div>

                <h3 className="text-2xl md:text-3xl font-bold text-white mb-6">
                  You Can't Know an Event Mattered Until After It Started
                </h3>

                {/* Excerpt */}
                <div className="prose prose-invert max-w-none">
                  <p className="text-lg text-slate-300 leading-relaxed mb-4">
                    The data worth keeping off a robot is sparse. A robot runs for hours and almost all of it is uneventful — the part you actually want is the disengagement, the near-miss, the moment the planner did something strange. A few seconds out of a long day.
                  </p>
                  
                  <p className="text-slate-300 leading-relaxed mb-6">
                    The trouble is that you can't recognize those seconds while they're happening. An event only becomes an event in hindsight. By the time a fault detector fires, or an operator hits the button, or your perception stack flags something off, the thing you wanted to capture has already begun. And a clip that starts at the moment of recognition is close to useless — what you need to understand why it happened is the lead-up, the seconds before. The approach to the intersection, not the screech.
                  </p>
                </div>

                {/* Expand/Collapse Button */}
                <button
                  onClick={() => setExpandedBlogPost(expandedBlogPost === 'original-launch' ? null : 'original-launch')}
                  className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 font-medium transition-colors mb-6"
                >
                  {expandedBlogPost === 'original-launch' ? (
                    <>
                      <span>Show Less</span>
                      <ChevronUp size={18} />
                    </>
                  ) : (
                    <>
                      <span>Read Full Post</span>
                      <ChevronDown size={18} />
                    </>
                  )}
                </button>

                {/* Full Content (Expandable) */}
                {expandedBlogPost === 'original-launch' && (
                  <div className="prose prose-invert max-w-none border-t border-slate-700 pt-6 mt-6">
                    <p className="text-slate-300 leading-relaxed mb-6">
                      So the preroll has to already exist. The only way to have the seconds before an event is to have already been writing them to disk when the event fired. There's no clever trigger that gets you around this. Either the data was on disk, or it's gone.
                    </p>

                    <h4 className="text-xl font-bold text-white mb-3 mt-8">Two Jobs, Two Change Rates</h4>
                    <p className="text-slate-300 leading-relaxed mb-4">
                      Once you accept that, the architecture falls out of it.
                    </p>
                    
                    <p className="text-slate-300 leading-relaxed mb-6">
                      Recording and deciding-what-matters are different jobs, and they change at different rates. Recording is something you want to set up once and never touch — on a robot, the recording path is the last thing you want churning, because every change to it is a chance to lose the data you can't get back. Deciding what matters, on the other hand, changes constantly: new fault conditions, new triggers, new windows, new reasons to keep a clip.
                    </p>
                    
                    <p className="text-slate-300 leading-relaxed mb-6">
                      Bolting those two jobs together means every change to your trigger logic risks the thing that captures your data. So we don't bolt them together.
                    </p>
                    
                    <p className="text-slate-300 leading-relaxed mb-6">
                      <span className="font-semibold text-white">momentedge-clipper</span> doesn't replace your recorder. It runs alongside an ordinary <code className="px-2 py-1 bg-slate-900 rounded text-blue-400 text-sm">ros2 bag record</code>, tails the growing MCAP file, and on each trigger cuts a standalone clip covering the window around the event — preroll included, because the data was already there. Recording stays rosbag2's job. Clipping is ours. The two never talk except through the file on disk.
                    </p>
                    
                    <p className="text-slate-300 leading-relaxed mb-6">
                      That gives you a recorder you can freeze. Pin it at a version, deploy it, and leave it alone — it has no opinion about your fault conditions, so it never needs to change when they do. The clipper itself is deliberately small for the same reason: decode-free byte copying, sequential IO, plain OS threads, no async runtime — we believe infra on the robot should be fast and stable.
                    </p>

                    <h4 className="text-xl font-bold text-white mb-3 mt-8">A Trigger Is Just a Topic</h4>
                    <p className="text-slate-300 leading-relaxed mb-4">
                      The clipper has no idea when something matters. It only answers one question: give me the window around time T.
                    </p>
                    
                    <p className="text-slate-300 leading-relaxed mb-6">
                      A trigger is anything that publishes a <code className="px-2 py-1 bg-slate-900 rounded text-blue-400 text-sm">momentedge_msgs/Trigger</code> — a name, a reference time, and a preroll/postroll window in nanoseconds. A fault detector can publish it. A watchdog can. An operator button can. Your own perception stack can. The clipper waits until the recording covers <code className="px-2 py-1 bg-slate-900 rounded text-blue-400 text-sm">[trigger_time - preroll, trigger_time + postroll]</code>, bulk-copies every message in that window into a standalone clip, and announces the file it wrote on <code className="px-2 py-1 bg-slate-900 rounded text-blue-400 text-sm">/events/momentedge/recorded</code>. The message bodies are never decoded — channels and schemas carry straight through.
                    </p>
                    
                    <p className="text-slate-300 leading-relaxed mb-6">
                      And because it's MCAP in and MCAP out, the clip is a complete, standard MCAP file. It opens in Foxglove, the mcap CLI, and rosbag2 replay. No vendor format on either side — the clip is just a smaller bag.
                    </p>

                    <h4 className="text-xl font-bold text-white mb-3 mt-8">What We Left Open</h4>
                    <p className="text-slate-300 leading-relaxed mb-4">
                      You'll notice the hard question isn't in here: who decides when to capture? For one robot, an operator button and a handful of fault conditions is plenty. Across a fleet, defining and managing what's worth keeping — and keeping those definitions consistent as the fleet and the failure modes evolve — is its own problem and worth a closer look.
                    </p>
                    
                    <p className="text-slate-300 leading-relaxed mb-6">
                      The clipper deliberately doesn't answer it. It captures the window you ask for, freezes on the robot, and stays out of the way. That's the part that should be stable and open.
                    </p>
                    
                    <p className="text-slate-300 leading-relaxed">
                      <a 
                        href="https://github.com/Momentedge/momentedge-clipper" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-400 hover:text-blue-300 underline font-medium"
                      >
                        github.com/Momentedge/momentedge-clipper
                      </a> — MCAP in, MCAP out, Apache-licensed. Clone it, point it at a bag, fire a trigger.
                    </p>
                  </div>
                )}

                {/* Footer with Link */}
                <div className="mt-6 pt-6 border-t border-slate-700">
                  <a
                    href="https://github.com/Momentedge/momentedge-clipper/releases/tag/v0.1.0"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-2 px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium transition-colors"
                  >
                    <ExternalLink size={18} />
                    <span>Read Full Release Notes</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Whitepapers Section */}
      <section id="whitepapers" className="relative py-6 md:py-8 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">
              Whitepapers
            </h2>

            <div className="space-y-6">
              {whitepapers.map((wp) => (
                <WhitepaperCard key={wp.slug} {...wp} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="relative pt-10 pb-16 md:pt-12 md:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-6">
              Want to Learn More?
            </h2>
            <p className="text-xl text-slate-300 mb-8">
              Discover how Momentedge can help your team capture high-quality robotics data efficiently.
            </p>
            <a
              href="mailto:hello@momentedge.xyz"
              className="inline-flex items-center px-8 py-4 bg-white text-slate-900 rounded-lg font-semibold hover:bg-slate-100 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              Get in Touch
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Resources;
